import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {AlertDialog} from '../../../components/common/dialogs/alertDialog';
import {useAppDispatch, useAppSelector} from '../../../helpers/reduxHelpers';
import {getAnalyticsSelectedViewId} from '../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {AnalyticsPanelViewsActionsEnum} from '../../../store/panels/analytics/views/analyticsPanelViewsActionsTypes';
import {getAnalyticsViewError} from '../../../store/panels/analytics/views/analyticsPanelViewsSelectors';
import {ReportHeaderFilters} from './filtersRow/reportHeaderFilters';
import {ReportHeaderTabs} from './tabsRow/reportHeaderTabs';
import {ReportHeaderTitleRow} from './titleRow/reportHeaderTitleRow';

/*
 * Props.
 */

interface ReportHeaderProps {
  title: ReactNode;
}

/*
 * Styles.
 */

const StyledHeaderWrapper = styled.div`
  padding-top: 27px;
  padding-bottom: 27px;

  > * {
    padding: 0 48px;
  }
`;

/*
 * Component.
 */

export const ReportHeader: FC<ReportHeaderProps> = ({title}) => {
  const selectedViewId = useAppSelector(getAnalyticsSelectedViewId);
  const errorStatus = useAppSelector(getAnalyticsViewError);
  const dispatch = useAppDispatch();

  const cleanError = () =>
    dispatch({
      type: AnalyticsPanelViewsActionsEnum.CLEAN_ERROR,
    });

  return (
    <StyledHeaderWrapper>
      <ReportHeaderTitleRow title={title} />
      <ReportHeaderTabs selectedViewId={selectedViewId} />
      <ReportHeaderFilters selectedViewId={selectedViewId} />
      {maybeDisplayConflictErrorDialog(cleanError, errorStatus)}
    </StyledHeaderWrapper>
  );
};

/*
 * Helpers.
 */

const maybeDisplayConflictErrorDialog = (onErrorDialogClose: Handler, errorStatus?: number) => {
  if (errorStatus !== 409) {
    return null;
  }

  return (
    <AlertDialog
      title={<Trans id="analytics-duplicate-view-error-dialog-title">Failed to save the view</Trans>}
      onRequestClose={onErrorDialogClose}
    >
      <Trans id="analytics-duplicate-view-error-dialog-message">A view with this name already exists.</Trans>
    </AlertDialog>
  );
};
